import { useState, useEffect } from 'react';
import CNPJMaskInput from '../masks/CNPJMaskInput';
import { formatCNPJ, unformatCNPJ } from '../../utils/cnpjUtils';
import AssociacoesFindModal from './AssociacoesFindModal';

function VinculoModal({
  show,
  onClose,
  onSaveSuccess,
  vinculo,
  associacaos,
  usuario,
}) {
  const [formData, setFormData] = useState({
    cnpj: '',
    cpf: '',
    tipo: '',
    situacao: 'ATIVO',
    nomeAssociacao: '',
  });
  const [showAssociacoesFindModal, setShowAssociacoesFindModal] =
    useState(false);
  const [errors, setErrors] = useState({});

  // Função auxiliar para remover formatação do CNPJ
  const unformatCNPJ = (cnpj) => {
    if (!cnpj) return '';
    return cnpj.toString().replace(/[^\d]/g, '');
  };

  // Inicializar dados quando o modal abrir
  useEffect(() => {
    if (show) {
      if (vinculo) {
        // Modo edição - usar dados do vinculo
        // Tenta buscar nome da associação em ordem: vinculo.associacao.nome, array, ou vinculo.nomeAssociacao
        let nomeAssociacao = '';
        
        if (vinculo.associacao?.nome) {
          nomeAssociacao = vinculo.associacao.nome;
        } else if (associacaos && associacaos.length > 0) {
          const vinculoCnpjUnformatted = unformatCNPJ(vinculo.cnpj);
          const associacaoEncontrada = associacaos.find(
            a => unformatCNPJ(a.cnpj) === vinculoCnpjUnformatted
          );
          nomeAssociacao = associacaoEncontrada?.nome || '';
        }
        

        
        setFormData({
          cnpj: vinculo.cnpj || '',
          cpf: vinculo.cpf || usuario?.cpf || '',
          tipo: vinculo.tipo || '',
          situacao: vinculo.situacao || 'ATIVO',
          nomeAssociacao: nomeAssociacao,
        });
      } else {
        // Modo criação
        setFormData({
          cnpj: '',
          cpf: usuario?.cpf || '',
          tipo: '',
          situacao: 'ATIVO',
          nomeAssociacao: '',
        });
      }
      setErrors({});
    }
  }, [show, vinculo, usuario, associacaos]);

  function handleChange(event) {
    const { name, value } = event.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value.toUpperCase(),
    }));

    // Limpar erro do campo quando o usuário digitar
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: '',
      }));
    }
  }

  function handleAssociacaoSelected(associacao) {
    setFormData(prevState => ({
      ...prevState,
      cnpj: associacao.cnpj,
      nomeAssociacao: associacao.nome,
    }));
    setShowAssociacoesFindModal(false);

    // Limpar erro do CNPJ se houver
    if (errors.cnpj) {
      setErrors(prev => ({
        ...prev,
        cnpj: '',
        nomeAssociacao: '',
      }));
    }
  }

  function validateForm() {
    const newErrors = {};

    if (!formData.cnpj.trim()) {
      newErrors.cnpj = 'CNPJ da associação é obrigatório';
    }

    if (!formData.tipo.trim()) {
      newErrors.tipo = 'Tipo de vínculo é obrigatório';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }

  function handleSubmit(event) {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    onSaveSuccess(formData);
  }

  function handleClose() {
    setFormData({
      cnpj: '',
      cpf: '',
      tipo: '',
      situacao: 'ATIVO',
      nomeAssociacao: '',
    });
    setErrors({});
    onClose();
  }

  // Encontrar a associação selecionada para mostrar o nome
  const associacaoSelecionada = associacaos?.find(
    a => a.cnpj === formData.cnpj
  );

  // Sincronizar o nome da associação quando o CNPJ mudar
  useEffect(() => {
    if (formData.cnpj && associacaoSelecionada) {
      setFormData(prev => ({
        ...prev,
        nomeAssociacao: associacaoSelecionada.nome,
      }));
    }
  }, [formData.cnpj, associacaoSelecionada]);

  if (!show) return null;

  return (
    <>
      <div
        className="modal show d-block"
        tabIndex="-1"
        style={{
          backgroundColor: 'rgba(0,0,0,0.5)',
          zIndex: 1055,
          position: 'fixed',
          inset: 0,
        }}
      >
        <div className="modal-dialog">
          <div className="modal-content">
            <div
              className="modal-header bg-success text-white"
              style={{ height: '30px' }}
            >
              <h5 className="modal-title mb-0">
                <i className="bi bi-link-45deg me-2"></i>
                {vinculo ? 'EDITAR VÍNCULO' : 'NOVO VÍNCULO'}
              </h5>
              <button
                type="button"
                className="btn-close btn-close-white"
                onClick={handleClose}
              ></button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="modal-body small">
                <div className="row g-2">
                  {/* Campo CPF (somente leitura) */}
                  <div className="col-md-5">
                    <label className="form-label mb-1">CPF *</label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      value={formData.cpf}
                      disabled
                      readOnly
                    />
                  </div>

                  {/* Campo Nome do Usuário (somente leitura) */}
                  <div className="col-md-7">
                    <label className="form-label mb-1">Usuário</label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      value={usuario?.nome || ''}
                      disabled
                      readOnly
                    />
                  </div>

                  {/* Campo CNPJ com botão de busca */}
                  <div className="col-md-5">
                    <label className="form-label mb-1">CNPJ *</label>
                    <div className="input-group input-group-sm">
                      <CNPJMaskInput
                        value={formatCNPJ(formData.cnpj || '')}
                        name="cnpj"
                        className={`form-control form-control-sm ${
                          errors.cnpj ? 'is-invalid' : ''
                        }`}
                        disabled
                        readOnly
                      />
                      <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm"
                        onClick={() => setShowAssociacoesFindModal(true)}
                      >
                        <i className="bi bi-search"></i>
                      </button>
                      {errors.cnpj && (
                        <div className="invalid-feedback">{errors.cnpj}</div>
                      )}
                    </div>
                  </div>

                  {/* Nome da Associação (somente leitura) */}
                  <div className="col-md-7">
                    <label className="form-label mb-1">Associação</label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      value={
                        formData.nomeAssociacao ||
                        associacaoSelecionada?.nome ||
                        ''
                      }
                      disabled
                      readOnly
                    />
                  </div>

                  {/* Tipo de Vínculo */}
                  <div className="col-md-6">
                    <label className="form-label mb-1">Tipo *</label>
                    <select
                      className={`form-select form-select-sm ${
                        errors.tipo ? 'is-invalid' : ''
                      }`}
                      value={formData.tipo}
                      onChange={handleChange}
                      name="tipo"
                    >
                      <option value="">Selecione...</option>
                      <option value="ADMIN">ADMINISTRADOR</option>
                      <option value="GEST">GESTOR</option>
                      <option value="USER">USUÁRIO</option>
                    </select>
                    {errors.tipo && (
                      <div className="invalid-feedback">{errors.tipo}</div>
                    )}
                  </div>

                  {/* Situação */}
                  <div className="col-md-6">
                    <label className="form-label mb-1">Situação</label>
                    <select
                      className="form-select form-select-sm"
                      value={formData.situacao}
                      onChange={handleChange}
                      name="situacao"
                    >
                      <option value="ATIVO">ATIVO</option>
                      <option value="INATIVO">INATIVO</option>
                    </select>
                  </div>
                </div>

                <div className="d-flex justify-content-end gap-2 mt-3">
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    onClick={handleClose}
                  >
                    <i className="bi bi-x-lg me-1"></i>
                    Cancelar
                  </button>
                  <button type="submit" className="btn btn-success btn-sm">
                    <i className="bi bi-check-lg me-1"></i>
                    {vinculo ? 'Atualizar' : 'Salvar'}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Modal de busca de associações */}
      <AssociacoesFindModal
        show={showAssociacoesFindModal}
        onHide={() => setShowAssociacoesFindModal(false)}
        onSelect={handleAssociacaoSelected}
      />
    </>
  );
}

export default VinculoModal;
